import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import { BoundOptionsContext } from '#/map/providers/BoundOptionsProvider';
import useGeolocation from '#/shared/hooks/useGeolocation';
import useMap from '#/map/hooks/useMap';
import useCurrentLocation from '#/shared/hooks/useCurrentLocation';
import GTM_HANDLES from '#/shared/gtm-handles';
import featureFlags from '#/shared/feature-flags';
import { selectMobileMode } from '#/user/slice';
import { zoomTo } from '#/map/utils/maplibre';
import Dialog from '#/shared/components/Dialog';
import MapLayerControlsContainer from '#/containers/MapLayerControlsContainer';
import useMapLayers from '#/map/hooks/useMapLayers';
import { THREE_DIMENSIONAL_LAYER } from '#/map/constants';
import { useAppSelector } from '#/state/hooks';
import { selectAlwaysShowMapControls } from '#/map/slice';
import MapButton from './MapButton';
import * as Styled from './styled';

const { mapLibre, mapCompass } = featureFlags;

const selectIsLayerSettingsOpen = (state: {
  user: { isLayerSettingsOpened: boolean };
}): boolean => state.user.isLayerSettingsOpened;

function MapLibreControls({
  toggleLayerSettings,
  mapSettingsButtonRef,
}: {
  toggleLayerSettings: () => void;
  mapSettingsButtonRef: React.RefObject<HTMLButtonElement>;
}): React.ReactElement {
  const intl = useIntl();
  const { setBaseLayerStreets, setBaseLayerThreeDimensional } = useMapLayers();

  const mobileMode = useAppSelector(selectMobileMode);
  const alwaysShowMapControls = useAppSelector(selectAlwaysShowMapControls);
  const { current: map } = useMap();
  const currentBaseLayer = useAppSelector(
    (s: { map: { baseLayer: string } }) => s.map.baseLayer
  );
  const is3DBaseLayer = currentBaseLayer === THREE_DIMENSIONAL_LAYER;
  const [is3DMode, setIs3DMode] = useState(false);
  const [mapBearing, setMapBearing] = useState(0);

  const { startPositionWatch, geolocationApproved } = useGeolocation();
  const { currentLocation } = useCurrentLocation();

  // Initialize 3D mode with pitch 45 if the initial state is 3D mode
  useEffect(() => {
    if (map && is3DBaseLayer) {
      map.easeTo({ pitch: 45 });
      setIs3DMode(true);
    }
  }, [map, is3DBaseLayer]);

  const sharePosition = useCallback(() => {
    startPositionWatch();
  }, [startPositionWatch]);

  const handleCurrentLocationClick = (): void => {
    if (mobileMode && !currentLocation) {
      sharePosition();
    } else {
      zoomTo(map, currentLocation, {});
    }
  };

  const disableCurrentLocationButton =
    (mobileMode && geolocationApproved === false) ||
    (!mobileMode && !currentLocation);

  const handleZoomIn = useCallback(() => {
    if (map) map.zoomIn();
  }, [map]);

  const handleZoomOut = useCallback(() => {
    if (map) map.zoomOut();
  }, [map]);

  const handleResetRotation = useCallback(() => {
    if (!map) return;

    if (map.getBearing() !== 0) {
      map.easeTo({ bearing: 0 });
    }
  }, [map]);

  useEffect(() => {
    if (!map) return undefined;

    const onRotate = (): void => {
      setMapBearing(map.getBearing());
    };

    const onPitch = (): void => {
      const pitch = map.getPitch();
      setIs3DMode(pitch > 0);
    };

    map.on('rotate', onRotate);
    map.on('pitch', onPitch);
    return () => {
      map.off('rotate', onRotate);
      map.off('pitch', onPitch);
    };
  }, [map]);

  const handleToggle3D = useCallback(() => {
    if (!map) return;

    if (is3DBaseLayer) {
      map.easeTo({ pitch: 0 });
      setIs3DMode(false);
      setBaseLayerStreets();
    } else {
      // Set the 3D mode state immediately for UI feedback
      setIs3DMode(true);

      // Create a one-time event handler for the moveend event
      const onMoveEnd = (): void => {
        // Change the base layer after the animation completes
        setBaseLayerThreeDimensional();
        // Remove the event listener to avoid memory leaks
        map.off('moveend', onMoveEnd);
      };

      // Add the event listener
      map.on('moveend', onMoveEnd);

      // Start the animation to pitch 45 degrees
      map.easeTo({ pitch: 45 });
    }
  }, [map, is3DBaseLayer, setBaseLayerStreets, setBaseLayerThreeDimensional]);

  // If alwaysShowMapControls has a value (user explicitly set it), use that value
  // Otherwise, default to: desktop=always show, mobile=don't show
  const shouldShowMapControls = alwaysShowMapControls ?? !mobileMode;

  return (
    <>
      {/* Only show compass in desktop mode when the mapCompass feature flag is enabled.
          The compass allows users to reset the map rotation and indicates current bearing. */}
      {shouldShowMapControls && mapCompass && (
        <Styled.ButtonGroup>
          <MapButton
            onClick={handleResetRotation}
            title={intl.formatMessage({ id: 'map.resetrotation' })}
            icon={
              <Styled.CompassIcon
                style={{ transform: `rotate(${mapBearing}deg)` }}
              />
            }
            data-gtm={GTM_HANDLES.map.resetRotationButton}
            disabled={false}
          >
            {intl.formatMessage({ id: 'map.resetrotationlabel' })}
          </MapButton>
        </Styled.ButtonGroup>
      )}
      <Styled.ButtonGroup>
        <MapButton
          onClick={handleToggle3D}
          title={intl.formatMessage({
            id: is3DMode ? 'map.toggle2d' : 'map.toggle3d',
          })}
          icon={<Styled.ThreeDIcon is3D={is3DMode} aria-hidden="true" />}
          data-gtm={GTM_HANDLES.map.toggle3DButton}
          disabled={false}
        >
          {intl.formatMessage({
            id: is3DMode ? 'map.toggle2dlabel' : 'map.toggle3dlabel',
          })}
        </MapButton>
        <MapButton
          onClick={handleCurrentLocationClick}
          title={intl.formatMessage({
            id: currentLocation
              ? 'map.currentlocationsetbuttontooltip'
              : 'map.currentlocationnotsetbuttontooltip',
          })}
          icon={
            <Styled.CurrentLocationIcon
              disabled={disableCurrentLocationButton}
            />
          }
          data-gtm={GTM_HANDLES.map.currentLocationButton}
          disabled={disableCurrentLocationButton}
        >
          {intl.formatMessage({
            id: currentLocation
              ? 'map.currentlocationlabel'
              : 'map.currentlocationlabeloff',
          })}
        </MapButton>
        {shouldShowMapControls && (
          <>
            <MapButton
              onClick={handleZoomIn}
              title={intl.formatMessage({ id: 'map.zoomin' })}
              icon={<Styled.ZoomInIcon />}
              data-gtm={GTM_HANDLES.map.zoomInButton}
              disabled={map ? map.getZoom() >= map.getMaxZoom() : true}
            >
              {intl.formatMessage({ id: 'map.zoominlabel' })}
            </MapButton>
            <MapButton
              onClick={handleZoomOut}
              title={intl.formatMessage({ id: 'map.zoomout' })}
              icon={<Styled.ZoomOutIcon />}
              data-gtm={GTM_HANDLES.map.zoomOutButton}
              disabled={map ? map.getZoom() <= map.getMinZoom() : true}
            >
              {intl.formatMessage({ id: 'map.zoomoutlabel' })}
            </MapButton>
          </>
        )}
      </Styled.ButtonGroup>
      <Styled.ButtonGroup>
        <MapButton
          onClick={toggleLayerSettings}
          title={intl.formatMessage({ id: 'map.mapsettingsbuttontooltip' })}
          icon={<Styled.LayersIcon />}
          data-gtm={GTM_HANDLES.map.layersButton}
          disabled={false}
          ref={mapSettingsButtonRef}
        >
          {intl.formatMessage({ id: 'map.mapsettingslabel' })}
        </MapButton>
      </Styled.ButtonGroup>
    </>
  );
}

function LeafletControls({
  toggleLayerSettings,
  mapSettingsButtonRef,
}: {
  toggleLayerSettings: () => void;
  mapSettingsButtonRef: React.RefObject<HTMLButtonElement>;
}): React.ReactElement {
  const intl = useIntl();
  const mobileMode = useAppSelector(selectMobileMode);
  const alwaysShowMapControls = useAppSelector(selectAlwaysShowMapControls);

  const options = useContext(BoundOptionsContext);
  const { startPositionWatch, geolocationApproved } = useGeolocation();
  const { currentLocation } = useCurrentLocation();
  const { current: map } = useMap();

  const sharePosition = useCallback(() => {
    startPositionWatch();
  }, [startPositionWatch]);

  const handleCurrentLocationClick = (): void => {
    if (mobileMode && !currentLocation) {
      sharePosition();
    } else {
      zoomTo(map, currentLocation, options);
    }
  };

  const handleZoomIn = useCallback(() => {
    if (map) map.zoomIn();
  }, [map]);

  const handleZoomOut = useCallback(() => {
    if (map) map.zoomOut();
  }, [map]);

  const disableCurrentLocationButton =
    (mobileMode && geolocationApproved === false) ||
    (!mobileMode && !currentLocation);

  // If alwaysShowMapControls has a value (user explicitly set it), use that value
  // Otherwise, default to: desktop=always show, mobile=don't show
  const shouldShowMapControls = alwaysShowMapControls ?? !mobileMode;

  return (
    <>
      <Styled.ButtonGroup>
        <MapButton
          onClick={handleCurrentLocationClick}
          title={intl.formatMessage({
            id: currentLocation
              ? 'map.currentlocationsetbuttontooltip'
              : 'map.currentlocationnotsetbuttontooltip',
          })}
          icon={
            <Styled.CurrentLocationIcon
              disabled={disableCurrentLocationButton}
            />
          }
          data-gtm={GTM_HANDLES.map.currentLocationButton}
          disabled={disableCurrentLocationButton}
        >
          {intl.formatMessage({
            id: currentLocation
              ? 'map.currentlocationlabel'
              : 'map.currentlocationlabeloff',
          })}
        </MapButton>
        {shouldShowMapControls && (
          <>
            <MapButton
              onClick={handleZoomIn}
              title={intl.formatMessage({ id: 'map.zoomin' })}
              icon={<Styled.ZoomInIcon />}
              data-gtm={GTM_HANDLES.map.zoomInButton}
              disabled={map ? map.getZoom() >= map.getMaxZoom() : true}
            >
              {intl.formatMessage({ id: 'map.zoominlabel' })}
            </MapButton>
            <MapButton
              onClick={handleZoomOut}
              title={intl.formatMessage({ id: 'map.zoomout' })}
              icon={<Styled.ZoomOutIcon />}
              data-gtm={GTM_HANDLES.map.zoomOutButton}
              disabled={map ? map.getZoom() <= map.getMinZoom() : true}
            >
              {intl.formatMessage({ id: 'map.zoomoutlabel' })}
            </MapButton>
          </>
        )}
      </Styled.ButtonGroup>
      <Styled.ButtonGroup>
        <MapButton
          onClick={toggleLayerSettings}
          title={intl.formatMessage({ id: 'map.mapsettingsbuttontooltip' })}
          icon={<Styled.LayersIcon />}
          data-gtm={GTM_HANDLES.map.layersButton}
          disabled={false}
          ref={mapSettingsButtonRef}
        >
          {intl.formatMessage({ id: 'map.mapsettingslabel' })}
        </MapButton>
      </Styled.ButtonGroup>
    </>
  );
}

export default function MapButtons({
  toggleLayerSettings,
}: {
  toggleLayerSettings: () => void;
}): React.ReactElement {
  const mapSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const intl = useIntl();
  const isLayerSettingsOpened = useAppSelector(selectIsLayerSettingsOpen);

  return (
    <Styled.MapButtonsWrapper>
      <Styled.MapButtonsInner>
        {mapLibre ? (
          <MapLibreControls
            toggleLayerSettings={toggleLayerSettings}
            mapSettingsButtonRef={mapSettingsButtonRef}
          />
        ) : (
          <LeafletControls
            toggleLayerSettings={toggleLayerSettings}
            mapSettingsButtonRef={mapSettingsButtonRef}
          />
        )}
        <Styled.DesktopAttributionContainer>
          <Styled.AttributionLinks />
        </Styled.DesktopAttributionContainer>
      </Styled.MapButtonsInner>
      {isLayerSettingsOpened && (
        <Dialog
          title={intl.formatMessage({ id: 'navbar.map-layers' })}
          closeButtonText={intl.formatMessage({
            id: 'navbar.layer-settings-close',
          })}
          closeDialog={toggleLayerSettings}
          openingButtonRef={mapSettingsButtonRef}
        >
          <MapLayerControlsContainer />
        </Dialog>
      )}
    </Styled.MapButtonsWrapper>
  );
}
